import type { Color, Layer, PixelOp } from "#canvas/types.ts";
import { Texture } from "pixi.js";

export interface LayerRender {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  texture: Texture;
}

interface RendererStore {
  subscribe: (cb: () => void) => () => void;
  getSnapshot: () => Map<string, LayerRender>;
  sync: (layers: Layer[], cols: number, rows: number) => void;
  repaint: (layers: Layer[]) => void;
  destroyAll: () => void;
}

export function makeOffscreen(cols: number, rows: number): LayerRender | null {
  const canvas = document.createElement("canvas");
  canvas.width = cols;
  canvas.height = rows;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = false;
  const texture = Texture.from(canvas);
  texture.source.scaleMode = "nearest";
  return { canvas, ctx, texture };
}

export function paintLayer(render: LayerRender, data: Color[][]): void {
  const { ctx, canvas, texture } = render;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  for (let r = 0; r < data.length; r++) {
    const row = data[r];
    if (!row) continue;
    for (let c = 0; c < row.length; c++) {
      const color = row[c];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(c, r, 1, 1);
    }
  }
  texture.source.update();
}

export function paintIndicators(render: LayerRender, pixels: PixelOp[]): void {
  const { ctx, canvas, texture } = render;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  for (const px of pixels) {
    if (!px.color) continue;
    ctx.fillStyle = px.color;
    ctx.fillRect(px.col, px.row, 1, 1);
  }
  texture.source.update();
}

export function createRendererStore(): RendererStore {
  let map = new Map<string, LayerRender>();
  const subs = new Set<() => void>();
  const notify = () => {
    map = new Map(map);
    for (const cb of subs) cb();
  };
  return {
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },
    getSnapshot() {
      return map;
    },
    sync(layers, cols, rows) {
      let structural = false;
      const ids = new Set<string>();
      for (const [id, r] of Array.from(map)) {
        if (r.canvas.width !== cols || r.canvas.height !== rows) {
          if (!r.texture.destroyed) r.texture.destroy(true);
          map.delete(id);
          structural = true;
        }
      }
      for (const layer of layers) {
        ids.add(layer.id);
        const existing = map.get(layer.id);
        if (existing && !existing.texture.destroyed) continue;
        const fresh = makeOffscreen(cols, rows);
        if (!fresh) continue;
        paintLayer(fresh, layer.data);
        map.set(layer.id, fresh);
        structural = true;
      }
      for (const id of Array.from(map.keys())) {
        if (!ids.has(id)) {
          const r = map.get(id);
          if (r && !r.texture.destroyed) r.texture.destroy(true);
          map.delete(id);
          structural = true;
        }
      }
      if (structural) notify();
    },
    repaint(layers) {
      for (const layer of layers) {
        const r = map.get(layer.id);
        if (r && !r.texture.destroyed) paintLayer(r, layer.data);
      }
    },
    destroyAll() {
      for (const r of map.values()) {
        if (!r.texture.destroyed) r.texture.destroy(true);
      }
      map = new Map();
      for (const cb of subs) cb();
    },
  };
}
